/**
 * Brand Core ページ (Vision / Mission / コアバリュー)
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business Vision・Mission・コアバリュー4項目の表示と編集 (canEdit のみ)。
 * @graph-connects api [calls] brandCore.get / brandCore.update
 */
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import type { BrandCoreInput } from '@cortex/domain-creative/web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useAuth } from '../components/auth-context.js';
import {
  EmptyState,
  Field,
  GhostButton,
  GlassCard,
  INPUT_CLASS,
  Modal,
  PrimaryButton,
  SectionHeading,
} from '../components/ui.js';

/**
 * Brand Core ページ。
 * @returns ページ要素
 * @graph-connects api [calls] brandCore.get / brandCore.update
 */
export function BrandCorePage(): JSX.Element {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const brandCoreQuery = useQuery(orpc.brandCore.get.queryOptions({ input: {} }));
  const brandCore = brandCoreQuery.data ?? null;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<BrandCoreInput | null>(null);

  const updateMutation = useMutation(
    orpc.brandCore.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.brandCore.key() });
        setEditing(false);
        showToast('success', 'Brand Core を更新しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );

  const openEditor = (): void => {
    if (brandCore) {
      setDraft({
        vision: brandCore.vision,
        mission: brandCore.mission,
        coreValues: brandCore.coreValues.map((value) => ({ ...value })),
      });
      setEditing(true);
    }
  };

  return (
    <div>
      <SectionHeading
        title="Brand Core"
        action={
          auth.canEdit && brandCore ? <GhostButton onClick={openEditor}>編集</GhostButton> : null
        }
      />
      {!brandCore ? (
        <EmptyState message="Brand Core が未登録です (seed 未実行)" />
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-4">
            <GlassCard>
              <div className="mb-2 text-micro font-bold uppercase tracking-label text-muted">
                Vision
              </div>
              <div className="text-headline font-bold leading-normal text-accent-text">
                {brandCore.vision}
              </div>
            </GlassCard>
            <GlassCard>
              <div className="mb-2 text-micro font-bold uppercase tracking-label text-muted">
                Mission
              </div>
              <div className="text-headline font-bold leading-normal text-foreground">
                {brandCore.mission}
              </div>
            </GlassCard>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[...brandCore.coreValues]
              .sort((a, b) => a.order - b.order)
              .map((value) => (
                <div
                  key={value.id}
                  className="flex items-center gap-2.5 rounded-lg border border-line-light bg-surface px-4 py-3"
                >
                  <span className="shrink-0 rounded bg-accent-soft px-1.5 py-0.5 font-mono text-caption font-bold text-accent-text">
                    {value.id}
                  </span>
                  <span className="text-body text-subtle">{value.text}</span>
                </div>
              ))}
          </div>
        </>
      )}

      <Modal
        open={editing}
        title="Brand Core を編集"
        onClose={() => {
          setEditing(false);
        }}
      >
        {draft && (
          <>
            <Field label="Vision">
              <input
                className={INPUT_CLASS}
                value={draft.vision}
                onChange={(event) => {
                  setDraft({ ...draft, vision: event.target.value });
                }}
              />
            </Field>
            <Field label="Mission">
              <input
                className={INPUT_CLASS}
                value={draft.mission}
                onChange={(event) => {
                  setDraft({ ...draft, mission: event.target.value });
                }}
              />
            </Field>
            {draft.coreValues.map((value, index) => (
              <Field key={value.id} label={`コアバリュー ${value.id}`}>
                <input
                  className={INPUT_CLASS}
                  value={value.text}
                  onChange={(event) => {
                    const coreValues = draft.coreValues.map((current, currentIndex) =>
                      currentIndex === index ? { ...current, text: event.target.value } : current,
                    );
                    setDraft({ ...draft, coreValues });
                  }}
                />
              </Field>
            ))}
            <div className="mt-4 flex justify-end gap-2">
              <GhostButton
                onClick={() => {
                  setEditing(false);
                }}
              >
                キャンセル
              </GhostButton>
              <PrimaryButton
                disabled={updateMutation.isPending}
                onClick={() => {
                  updateMutation.mutate(draft);
                }}
              >
                保存
              </PrimaryButton>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
